import { AccessibilityType } from '@yoop/server-model/event-accessibility';
import { CategoryType } from '@yoop/server-model/inventory-category';
import type { ResaleInventoryItem } from '@yoop/server-model/offer-group';
import { PayoutStatus as ServerPayoutStatus } from '@yoop/server-model/offer-group';
import { SeatConfigurationType } from '@yoop/server-model/seating';
import type { EventSecondaryListings } from '@yoop/server-model/user-activity';
import { isNotNilOrEmpty } from '@yoop/util';
import type { MyYoopResaleEvent, ResaleToken } from '@yoop/whitelabel-data';
import {
  EventAccessibility,
  PayoutStatus,
  SeatingType,
  TokenInventoryType,
} from '@yoop/whitelabel-data';
import { mediaFrom } from '@yoop/whitelabel-media';
import { getTokenType } from './token.helper';
import { eventStateFrom } from './event-common.helper';
import { tokenChoiceFrom } from '@yoop/whitelabel-token';

export const resaleEventFrom = (response: EventSecondaryListings): MyYoopResaleEvent => {
  const marketplace = response.marketplaceInformation?.[0];
  const resaleTokens =
    marketplace?.offerGroupInformation?.map((resaleOfferGroup): ResaleToken => {
      const payoutTotal = resaleOfferGroup.payoutReceipt?.totalAmount;
      return {
        id: resaleOfferGroup.id,
        category: {
          id: resaleOfferGroup.categoryDetailResponse.id,
          type: inventoryTypeFrom(resaleOfferGroup.categoryDetailResponse.type),
        },
        media: resaleOfferGroup.categoryDetailResponse.merchandiseMedia?.map(mediaFrom) ?? [],
        choices:
          resaleOfferGroup.categoryDetailResponse.choiceSets
            ?.map(tokenChoiceFrom)
            .filter(isNotNilOrEmpty) ?? [],
        listedPrice: resaleOfferGroup.reservePrice,
        payoutAmount: isNotNilOrEmpty(payoutTotal) ? parseFloat(payoutTotal) : undefined,
        type: getTokenType(resaleOfferGroup.categoryDetailResponse.choiceSets),
        description: resaleOfferGroup.description,
        status: payoutStatusFrom(resaleOfferGroup.payoutStatus),
        soldItems: resaleOfferGroup.soldInventoryItems?.map((item) =>
          resaleItemFrom(item, resaleOfferGroup.seatConfigurationType),
        ),
        unsoldItems: resaleOfferGroup.unsoldInventoryItems?.map((item) =>
          resaleItemFrom(item, resaleOfferGroup.seatConfigurationType),
        ),
      };
    }) ?? [];

  return {
    id: response.id,
    announcement: response.announcement,
    endDateTime: response.eventEndDate,
    locationName: response.locationName,
    vanityName: response.vanityName,
    startDateTime: response.eventStartDate,
    currency: response.currency,
    defaultMedia: mediaFrom(response.defaultMedia),
    accessibilityType:
      response.eventAccessibilityType === AccessibilityType.LIVE_ATTENDANCE
        ? EventAccessibility.InPerson
        : EventAccessibility.Virtual,
    name: response.shortName,
    media: response.media?.map(mediaFrom),
    state: eventStateFrom(response.state),
    payoutDate: response.payoutDate,
    resaleTokens,
  };
};

const resaleItemFrom = (
  { inventoryItemId, parentInventoryItemId, inventoryItemSeatingData }: ResaleInventoryItem,
  seatConfigurationType: SeatConfigurationType,
) => ({
  seating: {
    type:
      seatConfigurationType === SeatConfigurationType.SEATED
        ? SeatingType.Seated
        : SeatingType.GeneralAdmission,
    section: inventoryItemSeatingData?.section.code,
    seat: inventoryItemSeatingData?.seat.code,
    row: inventoryItemSeatingData?.row.code,
  },
  parentInventoryItemId,
  id: inventoryItemId,
});

const inventoryTypeFrom = (type: CategoryType | undefined): TokenInventoryType => {
  switch (type) {
    case CategoryType.EVENT_ACCESS:
      return TokenInventoryType.EventAccess;
    case CategoryType.MERCHANDISE:
      return TokenInventoryType.Merchandise;
    default:
      return TokenInventoryType.Addon;
  }
};

const payoutStatusFrom = (status: ServerPayoutStatus | undefined): PayoutStatus | undefined => {
  switch (status) {
    case ServerPayoutStatus.PENDING:
      return PayoutStatus.Pending;
    case ServerPayoutStatus.SUCCESS:
      return PayoutStatus.Completed;
    case ServerPayoutStatus.INITIATED:
      return PayoutStatus.Started;
    case ServerPayoutStatus.FAILED:
      return PayoutStatus.Failed;
  }
  return undefined;
};
